import type { I18n } from './i18n.js';

/**
 * Canvas radar for 7 axes. No external dependencies.
 * RTL flips by mirroring angle direction so axis labels keep their reading
 * order relative to the surrounding text.
 */
export function drawRadar(
  canvas: HTMLCanvasElement,
  scores: Record<string, number>,
  axes: readonly string[],
  i18n: I18n,
  highlight: readonly string[] = [],
): void {
  const dpr = Math.max(1, window.devicePixelRatio || 1);
  const rect = canvas.getBoundingClientRect();
  // Honour the actual rendered width so the chart never exceeds its container
  // on narrow viewports. Fall back to 360 only when measurement is unavailable
  // (e.g. canvas is detached during a unit test).
  const available = rect.width || 360;
  const size = Math.max(240, Math.min(520, available));
  canvas.width = size * dpr;
  canvas.height = size * dpr;
  canvas.style.width = `${size}px`;
  canvas.style.height = `${size}px`;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, size, size);

  // Pull themed colors from CSS custom properties so dark/light mode works.
  const cs = getComputedStyle(document.documentElement);
  const fg = (cs.getPropertyValue('--fg-muted') || '#555').trim();
  const border = (cs.getPropertyValue('--border') || '#ddd').trim();
  const accent = (cs.getPropertyValue('--accent') || '#3f5375').trim();

  const cx = size / 2;
  const cy = size / 2;
  // Reserve more room for axis labels at narrow widths — long multi-word
  // labels (e.g. "Proportional to need", "По прошлому поведению") otherwise
  // overflow the canvas on mobile.
  const labelInset = size < 380 ? 60 : 48;
  const radius = Math.max(64, size / 2 - labelInset);
  const n = axes.length;
  const rtl = i18n.dir === 'rtl';
  const highlightSet = new Set(highlight);
  // Adaptive label font: 10px on tiny mobiles, 13px on tablet+.
  const labelFontPx = Math.round(Math.max(10, Math.min(13, size * 0.035)));
  const lineHeight = Math.round(labelFontPx * 1.15);

  // Background rings.
  ctx.strokeStyle = withAlpha(border, 0.9);
  ctx.lineWidth = 1;
  for (let r = 1; r <= 4; r++) {
    const rr = (radius * r) / 4;
    ctx.beginPath();
    for (let i = 0; i < n; i++) {
      const a = axisAngle(i, n, rtl);
      const x = cx + rr * Math.cos(a);
      const y = cy + rr * Math.sin(a);
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.stroke();
  }

  // Spokes + labels.
  ctx.fillStyle = fg;
  ctx.textBaseline = 'middle';
  // Soft cap on label width so long localised strings wrap rather than
  // overflow. ~42% of canvas leaves room for the chart on the opposite side.
  const maxLabelWidth = Math.max(72, size * 0.42);
  for (let i = 0; i < n; i++) {
    const a = axisAngle(i, n, rtl);
    const x2 = cx + radius * Math.cos(a);
    const y2 = cy + radius * Math.sin(a);
    ctx.strokeStyle = withAlpha(border, 0.9);
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(x2, y2);
    ctx.stroke();

    const axis = axes[i]!;
    const lbl = i18n.t(`axes.${axis}.label`);
    const tx = cx + (radius + 16) * Math.cos(a);
    const ty = cy + (radius + 14) * Math.sin(a);
    ctx.fillStyle = highlightSet.has(axis) ? accent : fg;
    ctx.font = highlightSet.has(axis)
      ? `600 ${labelFontPx}px "Inter", ui-sans-serif, system-ui, sans-serif`
      : `${labelFontPx}px "Inter", ui-sans-serif, system-ui, sans-serif`;
    // Anchor labels to the side of the canvas they sit on so wrapped lines
    // grow inward (toward the chart edge) instead of off-screen.
    const cosA = Math.cos(a);
    ctx.textAlign = cosA > 0.2 ? 'left' : cosA < -0.2 ? 'right' : 'center';
    drawWrappedLabel(ctx, lbl, tx, ty, maxLabelWidth, lineHeight);
  }

  // Polygon (the participant's scores).
  ctx.beginPath();
  const vertices: Array<[number, number, number]> = [];
  for (let i = 0; i < n; i++) {
    const axis = axes[i]!;
    const v = Math.max(0, Math.min(100, scores[axis] ?? 50));
    const r = (radius * v) / 100;
    const a = axisAngle(i, n, rtl);
    const x = cx + r * Math.cos(a);
    const y = cy + r * Math.sin(a);
    vertices.push([x, y, v]);
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fillStyle = withAlpha(accent, 0.22);
  ctx.strokeStyle = withAlpha(accent, 0.9);
  ctx.lineWidth = 2;
  ctx.fill();
  ctx.stroke();

  // Vertex markers — emphasised for highlighted axes.
  for (let i = 0; i < n; i++) {
    const [x, y] = vertices[i]!;
    const axis = axes[i]!;
    const highlighted = highlightSet.has(axis);
    ctx.beginPath();
    ctx.arc(x, y, highlighted ? 4.5 : 3, 0, Math.PI * 2);
    ctx.fillStyle = highlighted ? accent : withAlpha(accent, 0.85);
    ctx.fill();
    if (highlighted) {
      ctx.strokeStyle = withAlpha(accent, 0.35);
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(x, y, 8, 0, Math.PI * 2);
      ctx.stroke();
    }
  }
}

/**
 * Greedy word-wrap for axis labels. Lines stay centred vertically around (x, y)
 * so the visual anchor remains the spoke endpoint regardless of line count.
 */
function drawWrappedLabel(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
): void {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth || !current) {
      current = candidate;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  const startY = y - ((lines.length - 1) * lineHeight) / 2;
  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i]!, x, startY + i * lineHeight);
  }
}

function axisAngle(i: number, n: number, rtl: boolean): number {
  // Start at the top (i=0), sweep clockwise (LTR) or counter-clockwise (RTL).
  const base = -Math.PI / 2;
  const step = (2 * Math.PI) / n;
  return base + (rtl ? -1 : 1) * i * step;
}

/** Accept 3/6-digit hex OR a CSS rgb() string; apply an alpha. */
function withAlpha(color: string, alpha: number): string {
  const c = color.trim();
  if (c.startsWith('#')) {
    const hex = c.slice(1);
    const full = hex.length === 3
      ? hex.split('').map((ch) => ch + ch).join('')
      : hex.padEnd(6, '0').slice(0, 6);
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  const m = c.match(/rgba?\(([^)]+)\)/);
  if (m) {
    const parts = m[1]!.split(',').map((s) => s.trim());
    const [r, g, b] = parts;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return c;
}
